/** Shared conversation transcript viewer for handover + CRM. */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GlassButton, GlassCard, Markdown, useI18n } from '@servicesai/ui';
import { channelLabel, MODALITY_LABELS } from '@servicesai/shared';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';

interface TranscriptMedia {
  filename: string;
  mime: string;
  url: string | null;
  documentId: string | null;
  kind?: string;
}

interface TranscriptMessage {
  role: string;
  direction: string;
  content: string | null;
  media?: TranscriptMedia[];
  modality?: string;
  createdAt: string;
}

function AttachmentBlock({ media }: { media: TranscriptMedia[] }) {
  if (media.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1.5">
      {media.map((item, i) => {
        const isImage = (item.mime ?? '').startsWith('image/');
        return (
          <div key={i} className="rounded-lg border border-ink/10 bg-white/60 p-2">
            {isImage && item.url ? (
              <a href={item.url} target="_blank" rel="noreferrer" className="block">
                <img
                  src={item.url}
                  alt={item.filename}
                  className="max-h-40 max-w-full rounded-md object-contain"
                />
              </a>
            ) : null}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
              <span className="font-semibold text-ink-soft">📎 {item.filename}</span>
              {item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-oasis underline-offset-2 hover:underline"
                >
                  Open
                </a>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ConversationTranscriptPage({
  conversationId,
  backTo,
  backLabel,
  handoverControls = false,
}: {
  conversationId: string;
  backTo: string;
  backLabel: string;
  handoverControls?: boolean;
}) {
  const { l } = useI18n();
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [meta, setMeta] = useState<Record<string, unknown> | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const load = () => {
    const fn = handoverControls ? admin().handoverDetail : admin().conversationTranscript;
    fn(conversationId)
      .then((data) => {
        const d = data as { conversation?: Record<string, unknown>; messages: TranscriptMessage[] };
        setMeta(d.conversation ?? null);
        setMessages(d.messages);
      })
      .catch(() => setError(true));
  };

  useEffect(load, [conversationId, handoverControls]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await admin().handoverReply(conversationId, text);
      setReply('');
      load();
    } finally {
      setBusy(false);
    }
  };

  const returnToAi = async () => {
    setBusy(true);
    try {
      await admin().handoverReturnToAi(conversationId);
      load();
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{l(S.loadFailed)}</div></div>;

  return (
    <div className="space-y-4">
      <Link to={backTo} className="text-xs font-semibold text-oasis">
        ← {backLabel}
      </Link>
      {meta && (
        <GlassCard className="p-4" strong>
          <div className="text-sm font-bold">
            {meta.channel ? l(channelLabel(meta.channel as string)) : '—'} ·{' '}
            {(meta.lang as string) ?? '—'}
            {Number(meta.voiceMessageCount ?? 0) > 0 ? (
              <span className="ms-2 text-xs font-bold text-camel-700">
                {l(MODALITY_LABELS.voice)} · {String(meta.voiceMessageCount)}
              </span>
            ) : null}
            {meta.activeReference ? (
              <span className="ms-2 font-mono text-xs" dir="ltr">
                {String(meta.activeReference)}
              </span>
            ) : null}
          </div>
        </GlassCard>
      )}
      <GlassCard className="max-h-[50vh] space-y-2 overflow-y-auto p-4">
        {messages.map((m, i) => {
          const media = m.media ?? [];
          const hasBody = Boolean(m.content) || media.length > 0;
          return (
            <div
              key={i}
              className={[
                'rounded-xl px-3 py-2 text-sm',
                m.direction === 'inbound' ? 'bg-aeblack-50' : 'bg-oasis/10',
              ].join(' ')}
              dir="auto"
            >
              <div className="text-[10px] font-bold uppercase text-ink-faint">
                {m.role}
                {m.modality === 'voice' ? (
                  <span className="ms-1.5 text-camel-700">· {l(MODALITY_LABELS.voice)}</span>
                ) : null}
              </div>
              {/* Outbound is the assistant's Markdown; inbound is whatever the
                  citizen typed, which is not markup and is not read as any. */}
              {m.content ? (
                m.direction === 'outbound' ? (
                  <Markdown>{m.content}</Markdown>
                ) : (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                )
              ) : null}
              <AttachmentBlock media={media} />
              {!hasBody ? '—' : null}
            </div>
          );
        })}
        {messages.length === 0 && <div className="text-xs text-ink-faint">{l(S.none)}</div>}
      </GlassCard>
      {handoverControls && (
        <GlassCard className="space-y-3 p-4">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={l(S.handoverReplyPlaceholder)}
            dir="auto"
            className="glass h-24 w-full resize-y rounded-xl p-3 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            <GlassButton variant="primary" disabled={busy || !reply.trim()} onClick={() => void sendReply()}>
              {l(S.handoverReply)}
            </GlassButton>
            <GlassButton variant="ghost" disabled={busy} onClick={() => void returnToAi()}>
              {l(S.handoverReturnAi)}
            </GlassButton>
          </div>
        </GlassCard>
      )}
    </div>
  );
}
